import React, { useRef, useState, useContext, useEffect } from "react";
import MessageAlert from "../../../components/messageAlert";
import buildLink from "../../../urls";
import { PlaySounds } from "../../../functions/playSounds";
import { CompanyContext } from "../../../contexts/companyCotext";
import { FaArrowLeft, FaSearch, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import Select, { components } from "react-select";
import Cookies from "js-cookie";
import ReactPaginate from "react-paginate";
import PointsLoader from "../../../components/PointsLoader";
import ProductHistoryDateRangeInput from "../../../components/productHistoryDateRangeInput";
import { axiosServer } from "../../../axiosServer";

const UsernameFilterMenu = (props) => {
  const { children, selectProps } = props;
  const handleMenuAction = (callback) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    callback?.();
  };

  return (
    <components.Menu {...props}>
      <components.MenuList {...props}>{children}</components.MenuList>
      <div className="grid grid-cols-2 gap-2 border-t border-dgrey1 bg-white p-2">
        <button
          type="button"
          onPointerDown={handleMenuAction(selectProps.onClearSelection)}
          className="col-span-2 rounded border border-dgrey1 bg-gray-50 px-3 py-2 text-sm text-dblack transition hover:bg-dgreyRate"
        >
          Clear all
        </button>
        <button
          type="button"
          onPointerDown={handleMenuAction(selectProps.onCancelSelection)}
          className="rounded border border-dgrey1 bg-white px-3 py-2 text-sm text-dblack transition hover:bg-dgreyRate"
        >
          Cancel
        </button>
        <button
          type="button"
          onPointerDown={handleMenuAction(selectProps.onApplySelection)}
          className="rounded bg-dblue px-3 py-2 text-sm text-white transition hover:opacity-90"
        >
          Apply
        </button>
      </div>
    </components.Menu>
  );
};

const UsernameValueContainer = (props) => {
  const selectedCount = props.getValue().length;

  return (
    <components.ValueContainer {...props}>
      {selectedCount > 1 ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-2">
          <span className="truncate text-center text-sm font-normal text-dblack">
            {selectedCount} users selected
          </span>
        </div>
      ) : null}
      {props.children}
    </components.ValueContainer>
  );
};

const UsernameMultiValue = (props) => {
  const selectedCount = props.selectProps.value?.length || 0;

  if (selectedCount > 1) {
    return null;
  }

  return <components.MultiValue {...props} />;
};

const UsernameClearIndicator = () => null;

const ProductHistory = () => {
  const initialHistoryFilters = {
    username: "",
    new_address: "",
    old_address: "",
    warehouse: "",
    warehouse_type: "",
    warehouse_quantity: "",
    restock_quantity: "",
    comment: "",
  };
  const { playErrorSound, playSuccessSound } = PlaySounds();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("0");
  const [users, setUsers] = useState([]);
  const history = useNavigate();
  const [loadingSearch, setLoadingSearch] = useState(false);
  const productId = useRef(null);
  const [message, setMessage] = useState("");
  const [showMessgSucc, setShowMessageSucc] = useState(false);
  const [showMessgErr, setShowMessageErr] = useState(false);
  const [productInf, setProductInf] = useState([]);
  const [stateCom] = useContext(CompanyContext);
  const [warehouses, setWarehouses] = useState([]);
  const [historyFilters, setHistoryFilters] = useState(initialHistoryFilters);
  const [appliedHistoryFilters, setAppliedHistoryFilters] = useState(
    initialHistoryFilters,
  );
  const [historyDateRange, setHistoryDateRange] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isUsernameMenuOpen, setIsUsernameMenuOpen] = useState(false);
  const [draftUsernameOptions, setDraftUsernameOptions] = useState([]);
  const draftUsernameOptionsRef = useRef([]);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(50); // Show 50 items per page
  const [totalPages, setTotalPages] = useState(0);

  const fetchWarehouses = async () => {
    try {
      const url = buildLink(`fetchtowarehouse`);
      const response = await axiosServer.get(url);

      if (response.data.success) {
        setWarehouses(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch warehouses:", err);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  // Set default warehouse to "Main" after warehouses are loaded
  useEffect(() => {
    if (warehouses.length > 0) {
      const mainWarehouse = warehouses.find(
        (warehouse) => warehouse.warehouse_name === "Main",
      );
      if (mainWarehouse) {
        setSelectedWarehouseId(mainWarehouse.warehouse_id);
        // Save to cookies for 1 day
        Cookies.set("selectedWarehousemanageupc", mainWarehouse.warehouse_id, {
          expires: 1,
        });
      }
    }
  }, [warehouses]);

  const formatDateForApi = (date) => {
    if (!date) {
      return "";
    }

    return new Date(date).toLocaleDateString("en-CA");
  };

  const buildHistoryUrl = (
    productValue,
    page,
    filtersValue = appliedHistoryFilters,
    dateRangeValue = historyDateRange,
  ) => {
    const trimmedWarehouseFilter = filtersValue.warehouse.trim();
    let historyUrl =
      buildLink("productWarehouseDescription") +
      `${encodeURIComponent(productValue)}` +
      `&user_id=${stateCom.userID}` +
      `&page=${page + 1}` +
      `&limit=${itemsPerPage}`;

    if (trimmedWarehouseFilter) {
      historyUrl += `&filter_warehouse=${encodeURIComponent(
        trimmedWarehouseFilter,
      )}`;
    } else if (
      selectedWarehouseId !== null &&
      String(selectedWarehouseId) !== "0"
    ) {
      historyUrl += `&filter_warehouse=${encodeURIComponent(
        selectedWarehouseId,
      )}`;
    }

    Object.entries(filtersValue).forEach(([key, value]) => {
      if (key === "warehouse") {
        return;
      }

      const trimmedValue = value.trim();
      if (trimmedValue) {
        historyUrl += `&filter_${key}=${encodeURIComponent(trimmedValue)}`;
      }
    });

    if (dateRangeValue?.startDate) {
      historyUrl += `&filter_date_start=${encodeURIComponent(
        formatDateForApi(dateRangeValue.startDate),
      )}`;
    }

    if (dateRangeValue?.endDate) {
      historyUrl += `&filter_date_end=${encodeURIComponent(
        formatDateForApi(dateRangeValue.endDate),
      )}`;
    }

    return historyUrl;
  };

  const getProducts = async (
    e,
    page = 0,
    productIdValue = null,
    filtersValue = appliedHistoryFilters,
    dateRangeValue = historyDateRange,
  ) => {
    setProductInf(null);
    if (e) {
      e.preventDefault();
    }
    setLoadingSearch(true);
    if (productId.current.value === "") {
      setMessage("Please enter a product ID or SKU.");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
      playErrorSound();
      setLoadingSearch(false);
      return;
    }
    const productValue = productIdValue || productId.current.value;

    try {
      if (productId.current) {
        productId.current.blur();
      }

      setHasSearched(true);
      const historyUrl = buildHistoryUrl(
        productValue,
        page,
        filtersValue,
        dateRangeValue,
      );

      const historyResponse = await axiosServer.get(historyUrl);
      if (historyResponse.data.success) {
        setShowMessageSucc(true);
        setTimeout(() => {
          setShowMessageSucc(false);
        }, 3000);
        playSuccessSound();
        setMessage();
        setProductInf(historyResponse.data.product_data);
        setUsers(historyResponse?.data?.users || []);
        setTotalPages(historyResponse.data.total_pages || 0);
        setLoadingSearch(false);
      } else {
        setLoadingSearch(false);
        setShowMessageErr(true);
        setTimeout(() => {
          setShowMessageErr(false);
        }, 3000);
        playErrorSound();
        setMessage(historyResponse?.data?.message);
        setProductInf([]);
        setTotalPages(0);
      }
    } catch (e) {
      setLoadingSearch(false);
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
      playErrorSound();
      setMessage(e.response?.data?.message || "Error occurred");
    }
  };

  function sanitizeHTML(html) {
    if (typeof window !== "undefined") {
      return DOMPurify.sanitize(html);
    } else {
      return html;
    }
  }

  const handleTrashClick = () => {
    productId.current.value = "";
    productId.current.focus();
  };

  function unescapeHTML(str) {
    if (!str) {
      return "";
    }
    return str.replace(/&([^;]+);/g, function (entity, entityCode) {
      var match;

      if (entityCode in htmlEntities) {
        return htmlEntities[entityCode];
        /*eslint no-cond-assign: 0*/
      } else if ((match = entityCode.match(/^#x([\da-fA-F]+)$/))) {
        return String.fromCharCode(parseInt(match[1], 16));
        /*eslint no-cond-assign: 0*/
      } else if ((match = entityCode.match(/^#(\d+)$/))) {
        return String.fromCharCode(~~match[1]);
      } else {
        return entity;
      }
    });
  }

  function formatCommentHTML(comment) {
    return sanitizeHTML(unescapeHTML(comment));
  }
  var htmlEntities = {
    nbsp: " ",
    cent: "¢",
    pound: "£",
    yen: "¥",
    euro: "€",
    copy: "©",
    reg: "®",
    lt: "<",
    gt: ">",
    quot: '"',
    amp: "&",
    apos: "'",
  };

  useEffect(() => {
    productId.current.focus();
  }, []);

  // Pagination logic - now using server-side pagination
  const currentItems = productInf || [];
  const pageCount = totalPages;
  const historyFilterInputClassName =
    "h-10 w-full rounded-md border border-dgrey1 px-2 text-sm font-normal";

  const applyFilters = (
    nextFilters = historyFilters,
    nextDateRange = historyDateRange,
  ) => {
    setAppliedHistoryFilters(nextFilters);
    setHistoryDateRange(nextDateRange);
    setCurrentPage(0);
    getProducts(null, 0, null, nextFilters, nextDateRange);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
    // Trigger a new API call with the new page
    const item = productId.current.value;
    if (item) {
      getProducts(
        null,
        event.selected,
        item,
        appliedHistoryFilters,
        historyDateRange,
      );
    }
  };

  const handleWarehouseChange = (selectedOption) => {
    const warehouseId = selectedOption ? selectedOption.value : null;
    setSelectedWarehouseId(warehouseId);

    // Save to cookies for 1 day
    if (warehouseId) {
      Cookies.set("selectedWarehousemanageupc", warehouseId, { expires: 1 });
    } else {
      Cookies.remove("selectedWarehousemanageupc");
    }
  };

  const handleHistoryFilterChange = (key, value) => {
    setHistoryFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleHistoryFilterKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyFilters();
    }
  };

  const handleDateFilterApply = (date) => {
    if (!date?.startDate || !date?.endDate) {
      applyFilters(historyFilters, null);
      return;
    }

    const nextDateRange = {
      startDate: date.startDate,
      endDate: date.endDate,
      key: date.keyName || date.key || "selection",
    };

    applyFilters(historyFilters, nextDateRange);
  };

  const onSubmit = (event) => {
    setAppliedHistoryFilters(historyFilters);
    setCurrentPage(0); // Reset to first page when searching
    getProducts(event, 0, null, historyFilters, historyDateRange);
  };

  const userOptions = Array.isArray(users)
    ? users
        .map((user) => {
          const value = user?.user_id ?? user?.id ?? user?.value;
          const label =
            user?.username ??
            user?.user_name ??
            user?.name ??
            user?.label ??
            value;

          if (value === null || value === undefined) {
            return null;
          }

          return {
            value: String(value),
            label: String(label),
          };
        })
        .filter(Boolean)
    : [];
  const historyWarehouseOptions = Array.isArray(warehouses)
    ? warehouses
        .map((warehouse) => {
          const value = warehouse?.warehouse_id;
          const label = warehouse?.warehouse_name;

          if (value === null || value === undefined || !label) {
            return null;
          }

          return {
            value: String(value),
            label: String(label),
          };
        })
        .filter(Boolean)
    : [];

  const selectedUsernameOptions = historyFilters.username
    ? historyFilters.username
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .map((selectedId) =>
          userOptions.find((option) => option.value === selectedId),
        )
        .filter(Boolean)
    : [];

  const syncDraftUsernameOptions = (nextOptions) => {
    const normalizedOptions = nextOptions || [];

    draftUsernameOptionsRef.current = normalizedOptions;
    setDraftUsernameOptions(normalizedOptions);
  };

  const handleUsernameMenuOpen = () => {
    syncDraftUsernameOptions(selectedUsernameOptions);
    setIsUsernameMenuOpen(true);
  };

  const handleUsernameMenuClose = () => {
    syncDraftUsernameOptions(selectedUsernameOptions);
    setIsUsernameMenuOpen(false);
  };

  const handleUsernameFilterChange = (selectedOptions) => {
    syncDraftUsernameOptions(selectedOptions);
  };

  const applyUsernameFilter = () => {
    const usernameIds = draftUsernameOptionsRef.current
      .map((option) => option.value)
      .join(",");

    const nextFilters = {
      ...historyFilters,
      username: usernameIds,
    };

    setHistoryFilters(nextFilters);
    setIsUsernameMenuOpen(false);
    applyFilters(nextFilters, historyDateRange);
  };

  const cancelUsernameFilter = () => {
    syncDraftUsernameOptions(selectedUsernameOptions);
    setIsUsernameMenuOpen(false);
  };

  const clearUsernameFilter = () => {
    const nextFilters = {
      ...historyFilters,
      username: "",
    };

    syncDraftUsernameOptions([]);
    setHistoryFilters(nextFilters);
    setIsUsernameMenuOpen(false);
    applyFilters(nextFilters, historyDateRange);
  };

  const usernameSelectValue = isUsernameMenuOpen
    ? draftUsernameOptions
    : selectedUsernameOptions;
  const selectedHistoryWarehouseOption = historyFilters.warehouse
    ? historyWarehouseOptions.find(
        (option) => option.value === historyFilters.warehouse,
      ) || null
    : null;

  const handleHistoryWarehouseFilterChange = (selectedOption) => {
    const nextFilters = {
      ...historyFilters,
      warehouse: selectedOption ? String(selectedOption.value) : "",
    };

    setHistoryFilters(nextFilters);
    applyFilters(nextFilters, historyDateRange);
  };

  return (
    <>
      {/* Header */}
      <div className="mb-5 sticky top-0 flex flex-col bg-white z-50">
        <div className="w-full bg-dbase h-16">
          <div className="container px-3 relative flex justify-between w-full text-white h-full">
            <div className="flex gap-7 flex-row justify-start">
              <button onClick={() => history(-1)}>
                <FaArrowLeft />
              </button>
              <h2 className="text-xl my-auto">Product History</h2>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <form onSubmit={onSubmit}>
          <div className="relative flex-1">
            <div className="mb-1">Warehouse</div>
            <Select
              placeholder="Select Warehouse"
              onChange={handleWarehouseChange}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderRadius: "7px",
                  borderColor: state.isFocused ? "grey" : "grey",
                  padding: "8px 5px",
                }),
              }}
              options={[
                { value: 0, label: "All" },
                ...warehouses.map((warehouse) => ({
                  value: warehouse.warehouse_id,
                  label: warehouse.warehouse_name,
                })),
              ]}
              value={
                selectedWarehouseId !== null &&
                selectedWarehouseId !== undefined
                  ? [
                      { value: 0, label: "All" },
                      ...warehouses.map((warehouse) => ({
                        value: warehouse.warehouse_id,
                        label: warehouse.warehouse_name,
                      })),
                    ].find(
                      (option) =>
                        String(option.value) === String(selectedWarehouseId),
                    )
                  : null
              }
              className="w-full"
            />
          </div>
          <div className="flex flex-col mt-3">
            <div className="mb-1">Product ID or SKU</div>
            <div className="flex flex-row border-dgrey1 rounded-lg text-center w-full gap-3">
              <div className=" relative flex flex-row border-dgrey1 rounded-lg text-center w-full gap-3">
                <input
                  ref={productId}
                  type="text"
                  className="border border-dgrey1 rounded-lg block p-2 flex-grow text-start w-full h-12 pr-10" // Add pr-10 to create space for the icon
                  placeholder="Product ID or SKU"
                />
                <FaTrash
                  onClick={() => handleTrashClick()}
                  className="absolute top-1/2 transform -translate-y-1/2 right-3 cursor-pointer opacity-20"
                />
              </div>

              <button className="border border-dgrey1 bg-dbase rounded-lg text-white px-5 h-12">
                <FaSearch />
              </button>
            </div>
          </div>
        </form>
      </div>

      {loadingSearch && (
        <div className="flex justify-center mt-6">
          <PointsLoader />
        </div>
      )}

      {hasSearched && !loadingSearch && (
        <div className="container px-3 mt-6">
          <div className="overflow-hidden rounded-[5px] border border-dgrey1 bg-white">
            {/* Table View - Responsive */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[800px]">
                <thead className="bg-white">
                  <tr className="border-b border-dgrey1">
                    <th className="px-12 py-3 text-center text-md font-bold text-dblack  tracking-wider border-r border-dgrey1">
                      Username
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      New Address
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      Old Address
                    </th>
                    <th className="px-12 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      Warehouse
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      Warehouse Type
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      Warehouse Quantity
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      Restock Quantity
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider border-r border-dgrey1">
                      Date Added
                    </th>
                    <th className="px-4 py-3 text-center text-md font-bold text-dblack tracking-wider">
                      Comment
                    </th>
                  </tr>
                  <tr className="border-b border-dgrey1">
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        options={userOptions}
                        value={usernameSelectValue}
                        onChange={handleUsernameFilterChange}
                        onFocus={handleUsernameMenuOpen}
                        onMenuOpen={handleUsernameMenuOpen}
                        onMenuClose={handleUsernameMenuClose}
                        menuIsOpen={isUsernameMenuOpen}
                        hideSelectedOptions={false}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        placeholder="Filter username"
                        className="w-full text-start"
                        components={{
                          Menu: UsernameFilterMenu,
                          ValueContainer: UsernameValueContainer,
                          MultiValue: UsernameMultiValue,
                          ClearIndicator: UsernameClearIndicator,
                        }}
                        onApplySelection={applyUsernameFilter}
                        onCancelSelection={cancelUsernameFilter}
                        onClearSelection={clearUsernameFilter}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            minHeight: "40px",
                            height: "40px",
                            minWidth: 0,
                            width: "100%",
                            borderRadius: "0.375rem",
                            borderColor: state.isFocused
                              ? "#9ca3af"
                              : "#d1d5db",
                            boxShadow: "none",
                          }),
                          valueContainer: (baseStyles) => ({
                            ...baseStyles,
                            height: "38px",
                            padding: "0 8px",
                            position: "relative",
                          }),
                          input: (baseStyles, state) => {
                            const selectedCount =
                              state.selectProps.value?.length || 0;

                            return {
                              ...baseStyles,
                              margin: 0,
                              padding: 0,
                              opacity: selectedCount > 1 ? 0 : 1,
                              width: selectedCount > 1 ? 0 : baseStyles.width,
                            };
                          },
                          indicatorsContainer: (baseStyles) => ({
                            ...baseStyles,
                            height: "38px",
                          }),
                          dropdownIndicator: (baseStyles) => ({
                            ...baseStyles,
                            padding: "6px",
                          }),
                          placeholder: (baseStyles) => ({
                            ...baseStyles,
                            color: "#b3afaf",
                            fontSize: "0.875rem",
                          }),
                          multiValue: (baseStyles) => ({
                            ...baseStyles,
                            maxWidth: "100%",
                          }),
                          multiValueLabel: (baseStyles) => ({
                            ...baseStyles,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }),
                          menu: (baseStyles) => ({
                            ...baseStyles,
                            zIndex: 20,
                            overflow: "hidden",
                            borderRadius: "0.75rem",
                            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
                          }),
                          menuList: (baseStyles) => ({
                            ...baseStyles,
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            maxHeight: "240px",
                          }),
                          option: (baseStyles, state) => ({
                            ...baseStyles,
                            padding: "10px 14px",
                            fontSize: "0.875rem",
                            color: "#1f2937",
                            backgroundColor: state.isSelected
                              ? "#dbeafe"
                              : state.isFocused
                                ? "#f3f4f6"
                                : "#ffffff",
                            cursor: "pointer",
                          }),
                          menuPortal: (baseStyles) => ({
                            ...baseStyles,
                            zIndex: 60,
                          }),
                        }}
                      />
                    </th>
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <input
                        type="text"
                        value={historyFilters.new_address}
                        onChange={(event) =>
                          handleHistoryFilterChange(
                            "new_address",
                            event.target.value,
                          )
                        }
                        onKeyDown={handleHistoryFilterKeyDown}
                        className={historyFilterInputClassName}
                        placeholder="Filter new address"
                      />
                    </th>
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <input
                        type="text"
                        value={historyFilters.old_address}
                        onChange={(event) =>
                          handleHistoryFilterChange(
                            "old_address",
                            event.target.value,
                          )
                        }
                        onKeyDown={handleHistoryFilterKeyDown}
                        className={historyFilterInputClassName}
                        placeholder="Filter old address"
                      />
                    </th>
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <Select
                        isClearable
                        options={historyWarehouseOptions}
                        value={selectedHistoryWarehouseOption}
                        onChange={handleHistoryWarehouseFilterChange}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        placeholder="Filter warehouse"
                        className="w-full text-start"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            minHeight: "40px",
                            height: "40px",
                            minWidth: 0,
                            width: "100%",
                            borderRadius: "0.375rem",
                            borderColor: state.isFocused
                              ? "#9ca3af"
                              : "#d1d5db",
                            boxShadow: "none",
                          }),
                          valueContainer: (baseStyles) => ({
                            ...baseStyles,
                            height: "38px",
                            padding: "0 8px",
                          }),
                          input: (baseStyles) => ({
                            ...baseStyles,
                            margin: 0,
                            padding: 0,
                          }),
                          indicatorsContainer: (baseStyles) => ({
                            ...baseStyles,
                            height: "38px",
                          }),
                          clearIndicator: (baseStyles) => ({
                            ...baseStyles,
                            padding: "6px",
                          }),
                          dropdownIndicator: (baseStyles) => ({
                            ...baseStyles,
                            padding: "6px",
                          }),
                          placeholder: (baseStyles) => ({
                            ...baseStyles,
                            color: "#b3afaf",
                            fontSize: "0.875rem",
                          }),
                          singleValue: (baseStyles) => ({
                            ...baseStyles,
                            fontSize: "0.875rem",
                          }),
                          menu: (baseStyles) => ({
                            ...baseStyles,
                            zIndex: 20,
                            overflow: "hidden",
                            borderRadius: "0.75rem",
                            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
                          }),
                          menuList: (baseStyles) => ({
                            ...baseStyles,
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            maxHeight: "240px",
                          }),
                          option: (baseStyles, state) => ({
                            ...baseStyles,
                            padding: "10px 14px",
                            fontSize: "0.875rem",
                            color: "#1f2937",
                            backgroundColor: state.isSelected
                              ? "#dbeafe"
                              : state.isFocused
                                ? "#f3f4f6"
                                : "#ffffff",
                            cursor: "pointer",
                          }),
                          menuPortal: (baseStyles) => ({
                            ...baseStyles,
                            zIndex: 60,
                          }),
                        }}
                      />
                    </th>
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <input
                        type="text"
                        value={historyFilters.warehouse_type}
                        onChange={(event) =>
                          handleHistoryFilterChange(
                            "warehouse_type",
                            event.target.value,
                          )
                        }
                        onKeyDown={handleHistoryFilterKeyDown}
                        className={historyFilterInputClassName}
                        placeholder="Filter type"
                      />
                    </th>
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <input
                        type="text"
                        value={historyFilters.warehouse_quantity}
                        onChange={(event) =>
                          handleHistoryFilterChange(
                            "warehouse_quantity",
                            event.target.value,
                          )
                        }
                        onKeyDown={handleHistoryFilterKeyDown}
                        className={historyFilterInputClassName}
                        placeholder="Filter quantity"
                      />
                    </th>
                    <th className="p-2 border-r border-dgrey1 bg-dgrey5">
                      <input
                        type="text"
                        value={historyFilters.restock_quantity}
                        onChange={(event) =>
                          handleHistoryFilterChange(
                            "restock_quantity",
                            event.target.value,
                          )
                        }
                        onKeyDown={handleHistoryFilterKeyDown}
                        className={historyFilterInputClassName}
                        placeholder="Filter restock"
                      />
                    </th>
                    <th className="min-w-[180px] p-2 border-r border-dgrey1 bg-dgrey5">
                      <ProductHistoryDateRangeInput
                        onSelect={handleDateFilterApply}
                        value={historyDateRange}
                        containerClassName="w-full"
                        inputClassName={historyFilterInputClassName}
                      />
                    </th>
                    <th className="p-2 bg-dgrey5">
                      <input
                        type="text"
                        value={historyFilters.comment}
                        onChange={(event) =>
                          handleHistoryFilterChange(
                            "comment",
                            event.target.value,
                          )
                        }
                        onKeyDown={handleHistoryFilterKeyDown}
                        className={historyFilterInputClassName}
                        placeholder="Filter comment"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y">
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          <div className="flex items-center justify-center">
                            {item.username}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          <div className="flex items-center justify-center">
                            {item.new_address ? (
                              <span>{item.new_address}</span>
                            ) : (
                              "-"
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          <div className="flex items-center justify-center">
                            {item.old_address ? (
                              <span>{item.old_address}</span>
                            ) : (
                              "-"
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-md">
                            {item.warehouse}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          {item.warehouse_type}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          {item.warehouse_quantity}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          {item.restock_quantity}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-md text-dblack border-r border-dgrey1 text-center">
                          <div className="text-md text-center">
                            {item.date_added
                              ? new Date(item.date_added).toLocaleDateString()
                              : "N/A"}
                            <br />
                            <span className="text-dblack text-md">
                              {item.date_added
                                ? new Date(item.date_added).toLocaleTimeString()
                                : ""}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-md text-dblack text-left align-top min-w-[320px] max-w-[420px]">
                          <div
                            title={unescapeHTML(item.comment)}
                            className="whitespace-pre-line break-words leading-6 [overflow-wrap:anywhere]"
                            dangerouslySetInnerHTML={{
                              __html: formatCommentHTML(item.comment),
                            }}
                          ></div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-6 text-center text-md text-dblack"
                      >
                        No history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="px-4 py-3">
              <ReactPaginate
                previousLabel="← Previous"
                nextLabel="Next →"
                pageCount={pageCount}
                onPageChange={handlePageClick}
                containerClassName="flex items-center justify-center space-x-2"
                pageClassName="inline-block"
                pageLinkClassName="px-3 py-2 text-sm text-dblack rounded-md"
                previousClassName="inline-block"
                previousLinkClassName="px-3 py-2 text-sm text-dblack rounded-md"
                nextClassName="inline-block"
                nextLinkClassName="px-3 py-2 text-sm text-dblack rounded-md"
                activeClassName="bg-dbase text-white rounded-md"
                activeLinkClassName="px-3 py-2 text-sm text-white bg-dbase rounded-md"
                disabledClassName="opacity-50 cursor-not-allowed"
                breakLabel="..."
                breakClassName="inline-block"
                breakLinkClassName="px-3 py-2 text-sm dblack"
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                forcePage={currentPage}
              />
            </div>
          )}
          {/* Add space below table and pagination */}
          <div className="pb-8"></div>
        </div>
      )}

      <MessageAlert
        message={message}
        type={showMessgErr ? "err" : "succ"}
        showMessg={showMessgErr || (showMessgSucc && true)}
      />
    </>
  );
};

export default ProductHistory;
